
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import {
  commonModule,
  jobsModule,
  actionTypesModule,
  itemsModule,
  companyModule,
} from "@/store/modules/store-accessor";
import AssigneeUserCombo from "@/components/Combobox/AssigneeUserCombo.vue";
import AssigneeMultiUserCombo from "@/components/Combobox/AssigneeMultiUserCombo.vue";
import AppointmentCalendar from "@/components/Calendar/AppointmentCalendar.vue";
import EventOverlappingWarning from "@/components/Alerts/EventOverlappingWarning.vue";
import InstallationItemWarrantyIndicator from "@/components/InstallationItemWarrantyIndicator.vue";
import { durationOptionByMinutes, getOptimalTextColor, isNumber, recurrenceTypesOptions, weekDaysOptions, recurrenceEndTypeOptions, recurrenceEndDate, setRecurrenceDayOnInput, recurrenceWeekToDays, showRecurrenceDaysOptions, recurrenceOccurMessage, primaryInstallationItemText } from "@/helpers/data";
import RecurrenceFields from "@/components/Appointments/RecurrenceFields.vue";
import RecurrenceCheckBox from "@/components/Appointments/RecurrenceCheckBox.vue";
import dayjs from "dayjs";
import { greekSearchFilter } from "@/helpers/data";

@Component({
  components: {
    AssigneeUserCombo,
    AssigneeMultiUserCombo,
    AppointmentCalendar,
    EventOverlappingWarning,
    InstallationItemWarrantyIndicator,
    RecurrenceFields,
    RecurrenceCheckBox,
  },
})
export default class AddAppointmentToJobModal extends Vue {
  @Prop()
  payload!: any;

  greekSearchFilter = greekSearchFilter;

  isAddingAppointment = false;

  dateScheduledAtMenu = false;
  menu4 = false;
  recurrenceEndTypeDateMenu = false;
  userChangedDaysOfWeek = false;
  installationItem = {} as Record<string, any>;

  jobModel = {
    type_id: { text: "", color: "", cost: 0, id: 0 },
    assignee_id: "",
    assignee_ids: [] as any[],
    appointment_status_id: 2,
    scheduled_start: "",
    scheduled_start_time: "",
    duration: this.durationCombo[1] || "",
    appointment_notes: "",
    installation_id: this.payload.job.installation_id,
    id: this.payload.job.id,
    installation_item_id: null,
    description: this.payload.job.description || "",
    send_client_email: false,
    // --- Recurrence ---
    is_recurrent: false,
    recurrence_type: null,
    interval: 1,
    days_of_week: null,
    end_type: null,
    occurrence_count: null,
    end_date: recurrenceEndDate(),
    recurrence_diff_jobs: true,
    // --- Recurrence ---
  };

  recurrenceTypesOptions() {
    return recurrenceTypesOptions(this.jobModel.interval).options;
  }

  weekDaysOptions() {
    return weekDaysOptions();
  }

  recurrenceEndTypeOptions() {
    return recurrenceEndTypeOptions();
  }

  recurrenceShowDateInput() {
    return (this.jobModel.end_type as any)?.value === 'until_date';
  }

  onScheduledStartInput() {
    this.dateScheduledAtMenu = false;
    setRecurrenceDayOnInput(this.jobModel, this.userChangedDaysOfWeek);
    this.recurrenceOccurMessage();
  }

  onRecurrenceTypeChange() {
    this.$nextTick(() => {
      recurrenceWeekToDays(this.jobModel);
    });
    return showRecurrenceDaysOptions(this.jobModel);
  }

  recurrenceOccurMessage() {
    return recurrenceOccurMessage(this.jobModel);
  }

  onDaysOfWeekChange() {
    this.userChangedDaysOfWeek = true;
  }

  updateAssignee(assignees: any[]) {
    if (assignees.length > 0) {
      this.jobModel.assignee_id = assignees[0];
    }
    this.jobModel.assignee_ids = assignees;
  }

  /** Align with Quick Add: today + current time when fields are empty, so the calendar matches the form. */
  prefillDefaultScheduleIfEmpty() {
    if (!this.jobModel.scheduled_start) {
      this.jobModel.scheduled_start = dayjs().format("YYYY-MM-DD");
    }
    if (!this.jobModel.scheduled_start_time) {
      this.jobModel.scheduled_start_time = dayjs().format("HH:mm");
    }
  }

  scheduledDateRule = [
    (val: any) => !!val || this.$i18n.t("scheduled_day_required"),
  ];

  userRule = [(val: any) => !!val || this.$i18n.t("user_required")];

  typeRule = [
    (val: any) =>
      (val || "").text.length > 0 || this.$i18n.t("action_required"),
  ];
  scheduledTimeRule = [
    (val: any) => !!val || this.$i18n.t("scheduled_time_required"),
  ];
  durationRule = [(val: any) => !!val || this.$i18n.t("duration_required")];
  notesRule = [
    (val: any) => {
      if (val != null)
        return val.length <= 1000 || this.$i18n.t("form_notes_length_rule");
      else return true;
    },
  ];
  descriptionRule = [
    (val: any) => val.length <= 80 || this.$i18n.t("jobDescriptionLengthRule"),
  ];
  recurrenceTypeRules = [
    (val: any) => !!val || this.$i18n.t('recurrenceTypeRequired'),
  ];

  recurrenceIntervalRules = [
    (val: any) => !!val || this.$i18n.t('recurrenceIntervalRequired'),
  ];

  recurrenceDaysRules = [
    (val: any) => val && val.length >= 1 || this.$i18n.t('recurrenceDaysRequired'),
  ];

  recurrenceEndTypeDateRules = [
    (val: any) => !!val || this.$i18n.t('recurrenceEndTypeDateRequired'),
  ];

  get durationCombo() {
    return commonModule.durationCombo;
  }

  get actionsCombo() {
    return actionTypesModule.actionTypesCombo;
  }

  get assigneeIdCombo() {
    return commonModule.personnelCombo;
  }

  get jobDescriptionIsEnabled() {
    return (companyModule.company?.preferences as any)?.job_description;
  }

  get installationPrimaryItems() {
    return itemsModule.installationPrimaryItems;
  }

  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

  primaryItemText(item: Record<string, any>) {
    return primaryInstallationItemText(item);
  }

  onlyNumbers(evt: any) {
    isNumber(evt);
  }

  setDurationFromActionType(actionType: any) {
    const duration = durationOptionByMinutes(this.durationCombo, actionType?.duration);
    if (duration) {
      (this.jobModel as any).duration = duration;
    }
  }

  @Watch("jobModel.type_id")
  onActionTypeChange(actionType: any) {
    this.setDurationFromActionType(actionType);
  }

  itemName(obj: any) {
    const combo: any = this.installationPrimaryItems;
    const foundItem = combo.find(
      (item: any) => item.id == obj.id
    );
    if (foundItem) {
      return foundItem;
    } else {
      if (obj.id) {
        this.installationPrimaryItems.push({ ...obj, disabled: true });
        return { ...obj, disabled: true };
      } else {
        return {};
      }
    }
  }

  async hideModal() {
    commonModule.hideModal();
  }

  async beforeMount() {
    await itemsModule.getInstallationPrimaryInventoryItems(
      this.payload.job.installation_id
    );

    this.installationItem = this.itemName({
      primary_items: this.payload.job.job_item,
      id: this.payload.job.installation_item_id,
      item_type_text: this.payload.job.item_type_text,
      item_make: this.payload.job.item_make,
      item_model: this.payload.job.item_model,
      item_serial_no: this.payload.job.item_serial_no,
    });
    this.jobModel.installation_item_id = this.installationItem.primary_items || "";

    this.jobModel.send_client_email = this.clientHasEmail ? this.isSendClientAppointmentEmailEnabled : false;

    const { defaultOption: defaultRecurrenceType } = recurrenceTypesOptions(this.jobModel.interval);
    (this.jobModel.recurrence_type as any) = defaultRecurrenceType;
  }

  async mounted() {
    commonModule.initSnackbar({});
    await commonModule.getPersonnelCombo();
    await commonModule.getActionCombo();
    await commonModule.getDurationCombo();
    await actionTypesModule.getActionTypesCombo();
    this.prefillDefaultScheduleIfEmpty();
  }

  get hasClientSelected() {
    return this.payload.installation && this.payload.installation.info;
  }

  get clientHasEmail() {
    if (!this.hasClientSelected) return false;
    const email = this.payload.installation.info.client_email;
    return email !== undefined && email !== null && email !== '';
  }

  get isSendClientAppointmentEmailEnabled() {
    return (companyModule.company?.preferences as Record<string, any>)?.send_client_appointment_email;
  }

  get disableSendEmailToClient() {
    return !(companyModule.company?.preferences as Record<string, any>)?.send_client_appointment_email || !this.hasClientSelected || !this.clientHasEmail;
  }

  doAddAppointment() {
    if ((this.$refs.form as Vue & { validate: () => boolean }).validate()) {
      this.isAddingAppointment = true;
      // Check if user removed the selected assignee_id
      if (
        typeof this.jobModel.assignee_id === "undefined" ||
        this.jobModel.assignee_id == "" ||
        this.jobModel.assignee_id == null
      ) {
        this.jobModel.assignee_id = "";
      }
      jobsModule.createAppointmentToJob(this.jobModel);
      this.hideModal();
    }
  }

  recurrenceEndDate() {
    return recurrenceEndDate();
  }

}
